import React, { useMemo } from 'react';
import {
  Box,
  Text,
  TextInput,
  Checkbox,
  SelectInput,
  DatePicker,
  Link,
  Button,
  Dropdown,
  DropdownOverlay,
  ActionList,
  ActionListItem,
} from '@razorpay/blade/components';
import { useFormWithProvider } from 'forms';
import { GenderOption } from 'components/EmployeeOnboarding/EmployeeSelfOnboarding/types';
import { CustomFields } from 'components/EmployeeOnboarding/EmployeeSelfOnboarding/components/CustomFields';
import { genderOptions } from 'components/EmployeeOnboarding/EmployeeSelfOnboarding/constants';
import {
  FormHeading,
  FormFieldsWrapper,
  StyledForm,
  FormFooterWrapper,
  BorderedBox,
} from 'components/EmployeeOnboarding/components/styles';
import { FormFields } from './types';
import { getPersonalDetailsValidationSchema } from './utils';
import { useHistory } from 'react-router-dom';
import { routePaths } from 'components/Routes/data';

export const PersonalDetails: React.FC = () => {
  const history = useHistory();

  const { register, validate, formData } = useFormWithProvider<FormFields>();

  const handleContinue = async () => {
    if (!validate(getPersonalDetailsValidationSchema())) {
      return;
    }
    history.push(routePaths.employeeOnboarding.contractorSelfOnboarding.reviewDetails);
  };

  const handleBack = () => {
    history.push(routePaths.employeeOnboarding.contractorSelfOnboarding.bankDetails);
  };

  const isFormValid = useMemo(
    () => validate(getPersonalDetailsValidationSchema(), { sideEffects: false }),
    [validate],
  );

  return (
    <StyledForm>
      <FormFieldsWrapper>
        <FormHeading
          title="Personal Details"
          description="Please provide your personal details to complete your profile."
        />
        <BorderedBox>
          <TextInput
            {...register('phoneNumber')}
            label="Phone Number"
            placeholder="Enter Phone Number"
            size="medium"
            prefix="+91"
          />

          <Box>
            <Text
              size="small"
              weight="medium"
              color="surface.text.gray.muted"
              marginBottom="spacing.3">
              Connect to WhatsApp
            </Text>
            <Checkbox {...register('enableWhatsapp', 'checkbox')} size="medium" isChecked={formData.enableWhatsapp}>
              Enable WhatsApp for my phone number
            </Checkbox>
            <Text size="small" color="surface.text.gray.muted" marginLeft="spacing.7">
              Claim reimbursements and get monthly payslips right in your WhatsApp account. Learn
              more about
              <Link
                href="https://razorpay.com/docs/payroll/integrations/whatsapp/"
                target="_blank"
                marginLeft="spacing.2"
                size="small"
                color="primary">
                WhatsApp integration.
              </Link>
            </Text>
          </Box>

          <Dropdown selectionType="single">
            <SelectInput
              {...register('gender', 'select')}
              label="Gender"
              placeholder="Select Gender"
              isRequired
              necessityIndicator="required"
            />
            <DropdownOverlay>
              <ActionList>
                {genderOptions.map((option: GenderOption) => (
                  <ActionListItem
                    key={option.value}
                    title={option.title}
                    value={option.value}
                    isSelected={formData.gender?.[0] === option.value}
                  />
                ))}
              </ActionList>
            </DropdownOverlay>
          </Dropdown>

          <DatePicker
            {...register('dob', 'date')}
            selectionType="single"
            label="Date of Birth"
            format="DD/MM/YYYY"
            size="medium"
            necessityIndicator="required"
            isRequired
            maxDate={new Date()}
          />

          <CustomFields register={register} formData={formData} />
        </BorderedBox>
      </FormFieldsWrapper>

      <FormFooterWrapper>
        <Button variant="tertiary" size="medium" onClick={handleBack}>
          Back
        </Button>
        <Button variant="primary" size="medium" onClick={handleContinue} isDisabled={!isFormValid}>
          Continue
        </Button>
      </FormFooterWrapper>
    </StyledForm>
  );
};
